<template>
    <div>
        <p class="text-sm font-medium text-iron-700">
            {{ $t("teams.team_users.assign_team_member") }}
        </p>
        <div class="mt-3 rounded-lg flex flex-col">
            <div
                class="gap-4 border-iron-200 px-2 py-2 flex items-center"
                v-for="user of usersWithAssignment"
                :key="user.id"
            >
                <ImageZaptimeShape :src="user.profile_photo_url" />

                <p class="text-sm grow">
                    {{ user.name }}
                </p>

                <Switch
                    :disabled="!canCurrentUserEditRoundRobin"
                    :checked="user.assigned"
                    @update:checked="handleAssignmenChange(user)"
                ></Switch>
            </div>
        </div>

        <RoundRobinPriority
            :users="assignedUsers"
            :disabled="!canCurrentUserEditRoundRobin"
            :event-type="eventType"
        ></RoundRobinPriority>
    </div>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { EventType, User } from "../types/EventType";
import { computed } from "vue";
import { Switch } from "@/components/ui/switch";
import RoundRobinPriority from "./RoundRobinPriority.vue";
import { UserWithAssignement } from "../types/UserWithAssignment";
import ImageZaptimeShape from "@/components/ImageZaptimeShape.vue";

const page = usePage();

const props = defineProps<{
    users: User[];
    eventType: EventType;
}>();

const assignedUsers = computed(() => {
    return usersWithAssignment.value.filter((u) => u.assigned);
});

// currently event type owner, team owner and admin can edit round robin
const canCurrentUserEditRoundRobin = computed(() => {
    const currentUserInUserTeam = props.users.find(
        // @ts-expect-error
        (user) => user.id === page.props.user.id,
    );

    // @ts-expect-error
    if (currentUserInUserTeam?.owned_teams?.length > 0) {
        return true;
    }

    // @ts-expect-error
    if (currentUserInUserTeam?.membership?.role === "admin") {
        return true;
    }

    // @ts-expect-error
    if (props.eventType.user_id === page.props.user.id) {
        return true;
    }

    return false;
});

const usersWithAssignment = computed(() => {
    const users: UserWithAssignement[] = [];

    props.users.forEach((user: User) => {
        const found = props.eventType.users.find(
            (userFind) => userFind.user_id === user.id,
        );

        if (found) {
            users.push({ ...user, assigned: true });
        } else {
            users.push({ ...user, assigned: false });
        }
    });

    return users;
});

function handleAssignmenChange(user: UserWithAssignement) {
    if (user.assigned) {
        removeUserFromEventType(user);
    } else {
        assignUserToEventType(user);
    }
}

function assignUserToEventType(user: User) {
    router.post(
        route("app.event-types.users.store", {
            eventType: props.eventType.id,
        }),
        {
            user_id: user.id,
        },
    );
}

function removeUserFromEventType(user: User) {
    router.delete(
        route("app.event-types.users.delete", {
            user: user.id,
            eventType: props.eventType.id,
        }),
    );
}
</script>
